import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { PaginationState } from '@tanstack/react-table'
import { getPaginationRowModel, getCoreRowModel } from '@tanstack/react-table'
import { RefreshCw, Download } from 'lucide-react'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'

import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { DateRangePicker  } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker';
import { currentUserAtom } from '@/store/auth.atom'

import { buildConversationColumns } from './columnsConversation'
import { AssignLabelModal } from './AssignLabelModal'
import { ConversationStatsCards } from './ConversationStatsCards'
import { formatEndDate, formatStartDate } from '@/utils/format'
import {
    fetchConversationStats,
    fetchTopProcedures,
    fetchHardQuestions
} from '@/api/feedback-conversation.api'
import type { HardQuestion } from '@/api/feedback-conversation.api';
import { useTableExport, exportHardQuestions } from '@/api/dowload-excel.api'

const DEFAULT_DATE_RANGE: DateRangeValue = {
    from: dayjs().startOf('month').toDate(),
    to: (dayjs().endOf('day').millisecond(0)).toDate(),
}

export function ConversationReportTab() {
    const currentUser = useAtomValue(currentUserAtom)
    const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(DEFAULT_DATE_RANGE)
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    })
    const [selectedRecord, setSelectedRecord] = useState<HardQuestion | null>(null)
    const [isExporting, setIsExporting] = useState(false)

    const apiParams = useMemo(() => {
        return {
            fromDate: formatStartDate(dateRange?.from || dayjs().startOf('month')),
            toDate: formatEndDate(dateRange?.to || dayjs()),
        }
    }, [dateRange, currentUser?.unitId])

    const { data: stats, refetch: refetchStats } = useQuery({
        queryKey: ['conversation-stats', apiParams],
        queryFn: ({ signal }) => fetchConversationStats(apiParams, signal),
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const { data: topProcedures = [], refetch: refetchTop } = useQuery({
        queryKey: ['top-procedures', apiParams],
        queryFn: ({ signal }) => fetchTopProcedures({ top: 10, ...apiParams }, signal),
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const { data: hardQuestions, isLoading: isTableLoading, refetch: refetchTable } = useQuery({
        queryKey: ['hard-questions', pageIndex, pageSize, apiParams],
        queryFn: ({ signal }) => fetchHardQuestions({
            page: pageIndex,
            size: pageSize,
            ...apiParams
        }, signal),
        refetchOnMount: 'always',
        refetchInterval: 30 * 60 * 1000,
    })

    const hasData = Boolean(hardQuestions?.items && hardQuestions.items.length > 0)


    const columns = useMemo(
        () => buildConversationColumns({
            pageIndex,
            pageSize,
            onAssignLabel: (record) => setSelectedRecord(record),
        }),
        [pageIndex, pageSize]
    )

    const handleRefresh = () => {
        refetchStats()
        refetchTop()
        refetchTable()
    }

    const { handleExport } = useTableExport<HardQuestion>(
            'Danh sách câu hỏi khó',
            columns,
            async (payload) => {
                try {
                    setIsExporting(true)
                    await exportHardQuestions(
                        { ...apiParams },
                        payload,
                        'danh-sach-cau-hoi-kho.xlsx',
                    )
                } finally {
                    setIsExporting(false)
                }
            },
        )

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="w-64">
                    <DateRangePicker
                        value={dateRange ?? DEFAULT_DATE_RANGE}
                        onChange={setDateRange}
                    />
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    className="h-[44px] w-[44px] !border-[#CE7A58] !text-[#CE7A58] hover:!bg-[#CE7A58]/10"

                    onClick={handleRefresh}
                >
                    <RefreshCw size={18} className={isTableLoading ? 'animate-spin' : ''} />
                </Button>
            </div>

            <ConversationStatsCards stats={stats} />

            <div className="flex flex-col lg:flex-row gap-3">
                <div className="w-full lg:w-1/3 rounded-xl border border-solid border-[#CCCCCC] bg-white shadow-sm flex flex-col">
                    <div className="px-4 py-2">
                        <h3 className="font-bold text-[#1E2F41] uppercase">Nội dung được hỏi nhiều</h3>
                    </div>
                    <div className="px-4 overflow-y-auto">
                        {topProcedures.length === 0 ? (
                            <div className="text-center text-gray-500 text-sm mt-4 py-4">Không có dữ liệu</div>
                        ) : (
                            topProcedures.map((item, index) => {
                                return (
                                    <div key={index} className="flex gap-4 text-sm items-center border border-b-solid border-gray-300">
                                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white text-sm font-medium ${index === 0 ? 'bg-[#CE7A58]' :
                                            index === 1 ? 'bg-[#CE7A58B2]' :
                                                index === 2 ? 'bg-[#CE7A5880]' :
                                                        'bg-[#1E2F4140]'
                                            }`}>
                                            {index + 1}
                                        </span>

                                        <p className="flex-1 text-[#333333] line-clamp-2" title={item.procedureName}>
                                            {item.procedureName}
                                        </p>

                                        <div className="flex items-center gap-4 whitespace-nowrap text-right">
                                            <div className="text-[#1E2F41] font-medium">
                                                {item.total} <span className="text-[#1E2F41B2] font-normal">lượt</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>
                </div>

                <div className="w-full lg:w-2/3 rounded-xl border border-solid border-[#CCCCCC] bg-white shadow-sm flex flex-col">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between px-4 py-2">
                            <h3 className="font-bold text-[#1E2F41] uppercase">Nội dung chưa thể phản hồi</h3>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-[32px] w-[32px] !border-[#CE7A58] !text-[#CE7A58] hover:!bg-[#CE7A58]/10"
                                onClick={handleExport}
                                disabled={!hasData || isExporting}
                            >
                                {isExporting ? (
                                    <RefreshCw size={15} className="animate-spin" /> 
                                ) : (
                                    <Download size={15} />
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-hidden flex flex-col">
                        <DataTable
                            columns={columns}
                            data={hardQuestions?.items ?? []}
                            isLoading={isTableLoading}
                            tableOptions={{
                                state: { pagination: { pageIndex, pageSize } },
                                onPaginationChange: setPagination,
                                getPaginationRowModel: getPaginationRowModel(),
                                getCoreRowModel: getCoreRowModel(),
                                manualPagination: true,
                                pageCount: hardQuestions?.totalPages ?? -1,
                            }}
                            footer={
                                <Pagination
                                    page={pageIndex + 1}
                                    pageCount={hardQuestions?.totalPages ?? 1}
                                    pageSize={pageSize}
                                    totalCount={hardQuestions?.total ?? 0}
                                    onPageChange={(p) => setPagination((s) => ({ ...s, pageIndex: p - 1 }))}
                                    onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
                                />
                            }
                        />
                    </div>
                </div>
            </div>

            <AssignLabelModal
                isOpen={!!selectedRecord}
                record={selectedRecord}
                onClose={() => setSelectedRecord(null)}
                onSuccess={() => {
                    setSelectedRecord(null)
                    refetchTable()
                }}
            />
        </div>
    )
}